
import React, { useEffect, useState } from 'react';
import { enhancedToast } from '@/utils/notifications/enhancedToast';
import TimetableHeader from '../components/timetable/TimetableHeader';
import TimetableCalendar from '../components/timetable/TimetableCalendar';
import UpcomingClasses from '../components/timetable/UpcomingClasses';
import TeacherAvailability from '../components/timetable/TeacherAvailability';
import { useTimetableData } from '../hooks/useTimetableData';
import { Button } from '../components/ui/button';
import { Loader2, RefreshCw, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const TeacherTimetable = () => {
  const { timetableData, isLoading, error, refreshData } = useTimetableData();
  const [lastRefreshed, setLastRefreshed] = useState(new Date());
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    const pollInterval = setInterval(() => {
      refreshData().then(() => {
        setLastRefreshed(new Date());
      }).catch(error => {
        console.error("Error refreshing timetable data:", error);
      });
    }, 3 * 60 * 1000);
    
    return () => clearInterval(pollInterval);
  }, [refreshData]);

  useEffect(() => {
    const checkUpcomingClasses = () => {
      if (!timetableData || timetableData.length === 0) return;
      
      const now = new Date();
      const threeMinutesFromNow = new Date(now.getTime() + (3 * 60 * 1000));
      
      timetableData.forEach(teacher => {
        if (!teacher.classes) return;
        
        teacher.classes.forEach(classItem => {
          const classTime = new Date(classItem.startTime);
          
          if (classTime > now && classTime <= threeMinutesFromNow && !classItem.alerted) {
            enhancedToast.warning(
              `${teacher.name} has a ${classItem.subject} class in ${Math.floor((classTime.getTime() - now.getTime()) / 60000)} minutes in Room ${classItem.room}`,
              "Upcoming Class Alert"
            );
            
            classItem.alerted = true;
          }
        });
      });
    };
    
    const intervalId = setInterval(checkUpcomingClasses, 60000);
    
    checkUpcomingClasses();
    
    return () => clearInterval(intervalId);
  }, [timetableData]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refreshData();
      setLastRefreshed(new Date());
      enhancedToast.success("Timetable data refreshed");
    } catch (error) {
      enhancedToast.error("Failed to refresh timetable data");
      console.error("Error refreshing timetable data:", error);
    } finally {
      setIsRefreshing(false);
    }
  };

  if (isLoading) return (
    <div className="p-8 flex flex-col items-center justify-center">
      <Loader2 className="h-10 w-10 animate-spin text-primary mb-4" />
      <p>Loading timetable data...</p>
    </div>
  );

  if (error) return (
    <div className="p-8 text-center">
      <div className="text-red-500 mb-4">Error loading timetable: {error.message}</div>
      <Button onClick={handleRefresh} variant="outline" className="flex items-center">
        <RefreshCw className="mr-2 h-4 w-4" />
        Retry
      </Button>
    </div>
  );

  return (
    <div className="container p-4 mx-auto">
      <Helmet>
        <title>Teacher Timetable - NALC Student Program & Fee Tracker</title>
      </Helmet>
      
      <div className="flex items-center justify-between mb-4">
        <TimetableHeader />
        <Button
          variant="outline"
          size="sm"
          onClick={handleRefresh}
          disabled={isRefreshing}
          className="flex items-center gap-2"
        >
          <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>
      
      <div className="text-xs text-muted-foreground mb-4 flex items-center justify-end gap-1">
        <Clock className="h-3 w-3" /> 
        Last updated: {lastRefreshed.toLocaleTimeString()}
      </div>
      
      <div className="space-y-6">
        <div className="w-full">
          <TimetableCalendar timetableData={timetableData || []} />
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <UpcomingClasses timetableData={timetableData || []} />
          <TeacherAvailability timetableData={timetableData || []} />
        </div>
      </div>
    </div>
  );
};

export default TeacherTimetable;
